import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../redux/apis/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [portalId, setPortalId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/hub/user/me', { _isAuthCheck: true });
            if (res?.data?.data?.user) {
                setUser(res.data.data.user);
                setPortalId(res.data.data.portal_id || null);
            } else {
                setUser(null);
                setPortalId(null);
            }
        } catch (_) {
            setUser(null);
            setPortalId(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    useEffect(() => {
        const handler = () => { setUser(null); setPortalId(null); navigate('/'); };
        window.addEventListener('auth:session-expired', handler);
        return () => window.removeEventListener('auth:session-expired', handler);
    }, [navigate]);

    const logout = useCallback(async () => {
        try {
            await api.post('/hub/auth/logout');
            localStorage.clear();
            sessionStorage.clear();
        } catch (_) {

        }
        setUser(null);
        setPortalId(null);
        localStorage.removeItem('isLoggedIn');
        sessionStorage.clear();
        navigate('/');
    }, [navigate]);

    useEffect(() => {
        const IDLE_MS = 30 * 60 * 1000; // 30 minutes
        let timer;
        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(logout, IDLE_MS);
        };
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearTimeout(timer);
            events.forEach(e => window.removeEventListener(e, reset));
        };
    }, [logout]);

    return (
        <AuthContext.Provider value={{
            user,
            portalId,
            isAdmin: user?.is_admin === true,
            isDemo: user?.is_demo === true,
            isAuthenticated: !!user,
            isLoading,
            fetchMe,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}
